// Solution Splitter run detail — follows the shared operation-detail structure
// (context → input → split result → prompts executed → per-run prompt detail),
// mirroring GenerationDetail/SolutionAnalysisDetail. Model runs are keyed by
// submissionId = jobId. The rich per-question split + pooled pages come from the
// live bulk photo-session (30-min TTL); after that only the durable job summary
// + LLM runs remain.
import { useEffect, useState } from 'react';
import { useParams, useNavigate , useLocation } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { ModelOutput } from '../../types';
import { Badge, Button } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import {
    OperationDetailPage,
    ContextGrid,
    UserProfileCard,
    MetadataCard,
    MetaItem,
    RunsTable,
    RunDetailCard,
    formatCost,
    formatLatency,
    totalRunCost,
    usePromptLookup,
    buildDefaultRunColumns,
    buildRunDetailHeading,
    buildRunSummary,
    getRunError,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import type { PersistedSplitSolution, SolutionSplitSessionView } from './solutionSplitterTypes';
import { SolutionSplitPages } from './SolutionSplitPages';
import { SolutionPageReading } from './SolutionPageReading';
import { OmrSheetReading } from './OmrSheetReading';
import { RegionCorrector, createRegionCorrectionApi } from '@shared-ui/solution-upload';
import { getAuthHeaders } from '../../api/fetchHelper';

/** The console acts for the student who owns the attempt — the correction, the
 *  re-read and the submission it produces are all theirs. */
const correctionApi = (studentId: string) =>
    createRegionCorrectionApi({ getAuthHeaders: () => getAuthHeaders() as Record<string, string> }, studentId);
import './SolutionAnalysisDetail.css';

function QualityBadge({ quality }: { quality: string }) {
    const variant = quality === 'readable' ? 'success'
        : quality === 'blank' || quality === 'irrelevant' ? 'default'
            : 'warning';
    return <Badge variant={variant} size="sm">{quality}</Badge>;
}

function SplitSolutionCard({ a, questionText }: { a: PersistedSplitSolution; questionText?: string }) {
    return (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-color, #2a2a2a)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <strong>Q{a.questionIndex + 1}</strong>
                <Badge variant={a.attempted ? 'success' : 'default'} size="sm">{a.attempted ? 'attempted' : 'no work'}</Badge>
                <QualityBadge quality={a.workQuality} />
                <span className="tag" title="Split confidence">split {Math.round(a.splitConfidence * 100)}%</span>
                {/* The confidence beside it can read 100% on a wrong match — it
                    did on the run that motivated this. "Did you read a number?"
                    is the honest signal, so it gets the loud treatment. */}
                {a.numberSource === 'inferred' && (
                    <Badge variant="warning" size="sm" title="No readable question number — the question was decided from position or content">
                        number inferred
                    </Badge>
                )}
                {a.numberSource === 'written' && (
                    <span className="tag" title="The question number was read off the page">number read</span>
                )}
                {a.pageIndices?.length > 0 && (
                    <span className="tag" title="Pooled page indices">pages {a.pageIndices.map(i => i + 1).join(', ')}</span>
                )}
                {a.boundingRects?.length > 0 ? (
                    <span className="tag" title="Detected regions (drawn on the pages above)">{a.boundingRects.length} box{a.boundingRects.length > 1 ? 'es' : ''}</span>
                ) : a.attempted && (
                    <span className="tag" title="Attempted but the model returned no region" style={{ color: 'var(--color-warning)' }}>no box</span>
                )}
            </div>
            {questionText && (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    <LatexText text={questionText.slice(0, 200)} />
                </div>
            )}
            {a.studentAnswer && (
                <div style={{ marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Answer: </span>
                    <LatexText text={a.studentAnswer} />
                    {/* Shown beside the answer, with the text it was read from,
                        because the point of collecting it is the comparison:
                        does the letter agree with the value, and did the attempt
                        get scored from either? Nothing selects an option off
                        this yet. */}
                    {a.markedOption && (
                        <Badge variant="default" size="sm" title={`Read from: ${a.markedOption.evidence}`}>
                            wrote ({a.markedOption.label})
                        </Badge>
                    )}
                </div>
            )}
            {a.extractedSteps?.length > 0 && (
                <ol style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 13 }}>
                    {a.extractedSteps.map((s, i) => (
                        <li key={i} style={{ opacity: s.crossedOut ? 0.5 : 1, textDecoration: s.crossedOut ? 'line-through' : 'none' }}>
                            <LatexText text={s.step} />
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}

/**
 * How well we could READ this upload, as opposed to what we concluded from it.
 *
 * Three things that were previously invisible until someone downloaded the page
 * blobs by hand: pages whose question numbers were cropped or absent, blocks the
 * model matched without a number to go on, and the answers the student declared
 * outright. The last of those is the interesting column — an answer with no
 * corresponding split entry means the student says they answered a question the
 * mapping left empty, which is the signature of a mis-attribution.
 *
 * Nothing here changes a mark. It exists so the numbers can be read off real
 * uploads before anything is allowed to.
 */
function ReadingQuality({ session }: { session: SolutionSplitSessionView }) {
    const { pageSurvey, answerSheet, splitSolutions } = session;
    if (!pageSurvey?.length && !answerSheet?.length) return null;

    const illegible = (pageSurvey || []).filter(p => p.numbersLegible === false);
    // 'unclear' is not a finding — it is the model declining to call it, and
    // counting it as sideways would inflate exactly the number we are checking.
    const notUpright = (pageSurvey || []).filter(
        p => p.textOrientation && p.textOrientation !== 'upright' && p.textOrientation !== 'unclear',
    );
    const inferred = splitSolutions.filter(a => a.numberSource === 'inferred');
    const attributed = new Set(splitSolutions.map(a => a.questionIndex));
    const orphaned = (answerSheet || []).filter(a => !attributed.has(a.questionIndex));

    return (
        <div className="card">
            <h3>Reading quality</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <Badge variant={illegible.length ? 'warning' : 'default'} size="sm">
                    {illegible.length} of {(pageSurvey || []).length} pages with unreadable numbers
                </Badge>
                <Badge variant={notUpright.length ? 'warning' : 'default'} size="sm">
                    {notUpright.length} of {(pageSurvey || []).length} pages not upright
                </Badge>
                <Badge variant={inferred.length ? 'warning' : 'default'} size="sm">
                    {inferred.length} question{inferred.length === 1 ? '' : 's'} matched without a number
                </Badge>
                <Badge variant={orphaned.length ? 'warning' : 'default'} size="sm">
                    {orphaned.length} declared answer{orphaned.length === 1 ? '' : 's'} with no work attributed
                </Badge>
            </div>

            {(pageSurvey || []).length > 0 && (
                <ol style={{ margin: '4px 0 10px', paddingLeft: 20, fontSize: 13 }}>
                    {(pageSurvey || []).map(p => (
                        <li key={p.pageIndex} style={{ marginBottom: 4 }}>
                            {p.numbersLegible === false && (
                                <Badge variant="warning" size="sm" title="Question numbers cropped, obscured or absent">numbers unreadable</Badge>
                            )}
                            {p.textOrientation && p.textOrientation !== 'upright' && (
                                <Badge
                                    variant={p.textOrientation === 'unclear' ? 'default' : 'warning'}
                                    size="sm"
                                    title="How the splitter saw this page. Compare against the landscape-frame check the upload dialog acts on — nothing rotates a page off this."
                                >
                                    {p.textOrientation.replace('_', ' ')}
                                </Badge>
                            )}
                            <span style={{ marginLeft: 6, color: 'var(--text-secondary)' }}>{p.blocks}</span>
                        </li>
                    ))}
                </ol>
            )}

            {(answerSheet || []).length > 0 && (
                <>
                    <h4 style={{ margin: '10px 0 4px', fontSize: 13 }}>Answers the student declared</h4>
                    <div style={{ fontSize: 13 }}>
                        {(answerSheet || []).map((a, i) => {
                            const split = splitSolutions.find(s => s.questionIndex === a.questionIndex);
                            // Two readings of one page. Equal strings are not the
                            // only agreement worth having, but a difference here
                            // is always worth a look.
                            const disagrees = !!split?.studentAnswer
                                && split.studentAnswer.trim().toLowerCase() !== a.answer.trim().toLowerCase();
                            return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', flexWrap: 'wrap' }}>
                                    <strong style={{ minWidth: 44 }}>Q{a.questionIndex + 1}</strong>
                                    <span className="tag" title="The number the student wrote">wrote &quot;{a.questionLabel}&quot;</span>
                                    <LatexText text={a.answer} />
                                    <span className="tag" title="Legibility of the mark, not correctness">{Math.round(a.confidence * 100)}%</span>
                                    <span className="tag" title="Pooled page index">p{a.pageIndex + 1}</span>
                                    {!split && <Badge variant="warning" size="sm" title="The student declared an answer here but no work was attributed to this question">no work attributed</Badge>}
                                    {disagrees && (
                                        <Badge variant="warning" size="sm" title={`The working for this question ended at "${split?.studentAnswer}"`}>
                                            disagrees with working
                                        </Badge>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

export function SolutionSplitterDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
    // Open when someone here decides a question was read off the wrong region.
    const [correcting, setCorrecting] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ['solution-split', id],
        queryFn: () => adminApi.getSolutionSplit(id!),
        enabled: !!id,
        refetchInterval: (q) => {
            const s = q.state.data?.run?.status ?? q.state.data?.job?.status;
            return s === 'running' || s === 'queued' ? 3000 : false;
        },
    });

    // Standard model-run table + per-run detail (keyed by submissionId = jobId).
    const { data: runs = [] } = useQuery({
        queryKey: ['modelRuns', id],
        queryFn: () => adminApi.getModelRuns(id!),
        enabled: !!id,
        // Rows exist from each call's start; refresh while one is in flight.
        refetchInterval: (q) => (q.state.data?.some(r => r.status === 'running') ? 5000 : false),
    });
    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });
    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    useEffect(() => {
        if (selectedRunId || runs.length === 0) return;
        const primary = runs.find(r => r.isPrimary) ?? runs[0];
        if (primary) setSelectedRunId(primary.id);
    }, [runs, selectedRunId]);

    const goBack = () => navigate('/submissions?tab=solutionsplit');

    if (isLoading) return <div className="operation-detail-page"><div className="loading">Loading solution split…</div></div>;
    if (error || !data) {
        return (
            <div className="operation-detail-page">
                <div className="error">Error loading solution split: {(error as Error)?.message || 'Not found'}</div>
                <button onClick={goBack}>Back to Solution Splitter</button>
            </div>
        );
    }

    const { job, run, result, user, session, source, attempt } = data;
    // Correcting needs the attempt (whose pages these are) and the student who
    // owns it: the console acts on their behalf and the work stays theirs.
    const correctionTarget = attempt?.testSessionId && user?.id
        ? { testSessionId: attempt.testSessionId, studentId: user.id }
        : null;
    // Durable record first; the async job is only joined in while it lives.
    const startedAt = run?.startedAt ?? job?.startedAt;
    const completedAt = run?.completedAt ?? job?.completedAt;
    const status = run?.status ?? job?.status ?? 'unknown';
    const errorMessage = run?.error ?? job?.lastError?.message;
    const latencyMs = startedAt && completedAt
        ? new Date(completedAt).getTime() - new Date(startedAt).getTime()
        : undefined;
    const questionTextByIndex = new Map((session?.questions || []).map(q => [q.questionIndex, q.questionText]));

    const promptLookup = { getPromptName, getPromptType };
    const runColumns = buildDefaultRunColumns<ModelOutput>(promptLookup, {
        returnTo: { label: 'Solution Splitter Detail' },
    });
    const selectedRun = runs.find(r => r.id === selectedRunId);
    const runDetailHeading = selectedRun && buildRunDetailHeading(selectedRun, promptLookup);
    const runSummary = selectedRun && buildRunSummary(selectedRun);
    const runError = selectedRun ? getRunError(selectedRun) : undefined;
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRunRawResponse = selectedRun ? getRawOutputText(selectedRun) : null;
    const parsedHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRunRawResponse));
    const parsedBody = selectedRun && parsedHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRunRawResponse}
        />
    ) : undefined;

    return (
        <>
        <OperationDetailPage
            title="Solution Splitter Details"
            id={job?.id ?? run?.id ?? ''}
            backLabel="Solution Splitter"
            navSlot={opNav && <OperationNavStrip nav={opNav} noun="Split" />}
            onBack={goBack}
            context={
                <ContextGrid
                    left={
                        <UserProfileCard
                            user={{ id: user?.id, name: user?.name, email: user?.email, grade: user?.grade }}
                            onViewUser={uid => navigate(`/users/${uid}`)}
                        />
                    }
                    right={
                        <MetadataCard title="Run Summary">
                            <MetaItem label="Status"><span className={`tag status-${status}`}>{status}</span></MetaItem>
                            <MetaItem label="Split">{result ? `${result.attempted}/${result.totalQuestions}` : '—'}</MetaItem>
                            <MetaItem label="Pages">{result?.pages ?? '—'}</MetaItem>
                            <MetaItem label="Provider"><span className="tag">{result?.llm?.provider ?? '—'}</span></MetaItem>
                            <MetaItem label="Tokens">{result?.llm ? `${result.llm.inputTokens} → ${result.llm.outputTokens}` : '—'}</MetaItem>
                            {/* Every call of the job — the split, the page reader, the
                                answer list — not the split pipeline's alone. */}
                            <MetaItem label="Cost">{formatCost(totalRunCost(runs) ?? result?.llm?.costUsd)}</MetaItem>
                            <MetaItem label="Latency">{formatLatency(latencyMs)}</MetaItem>
                            <MetaItem label="Attempts">{job ? `${job.attempts}/${job.maxAttempts}` : '—'}</MetaItem>
                            <MetaItem label="Queued">{job ? <span className="datetime">{format(new Date(job.queuedAt), 'MMM dd, HH:mm:ss')}</span> : '—'}</MetaItem>
                            {completedAt && (
                                <MetaItem label="Completed"><span className="datetime">{format(new Date(completedAt), 'MMM dd, HH:mm:ss')}</span></MetaItem>
                            )}
                            {errorMessage && (
                                <MetaItem label="Error"><span className="tag status-failed">{errorMessage}</span></MetaItem>
                            )}
                        </MetadataCard>
                    }
                />
            }
            input={
                <>
                    <div className="card">
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <h3>Uploaded Pages {session ? `(${session.pageUrls.length})` : ''} — detected regions</h3>
                            {/* A correction is a fact about an ATTEMPT, so it is
                                offered only where this run records which one. */}
                            {correctionTarget && (
                                <Button variant="secondary" size="sm" onClick={() => setCorrecting(true)}>
                                    Correct a question&apos;s region
                                </Button>
                            )}
                        </div>
                        {session && session.pageUrls.length > 0 ? (
                            <SolutionSplitPages
                                pageUrls={session.pageUrls}
                                splitSolutions={session.splitSolutions}
                            />
                        ) : (
                            <p className="muted">{source === 'unavailable' ? 'No durable record for this run — it predates run records, and its upload session has expired.' : 'No pages recorded.'}</p>
                        )}
                    </div>
                    <div className="card">
                        <h3>Question Set {session ? `(${session.questions.length})` : ''}</h3>
                        {session && session.questions.length > 0 ? (
                            <ol style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                                {session.questions.map(q => (
                                    <li key={q.questionId} style={{ marginBottom: 6, fontSize: 13 }}>
                                        <LatexText text={q.questionText} />
                                        {q.questionType && <span className="tag" style={{ marginLeft: 6 }}>{q.questionType}</span>}
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <p className="muted">{source === 'unavailable' ? 'No durable record for this run.' : 'No questions recorded.'}</p>
                        )}
                    </div>
                </>
            }
            operationSummary={
                <>
                {session && <ReadingQuality session={session} />}
                {session?.omrSheets && session.omrSheets.length > 0 && <OmrSheetReading sheets={session.omrSheets} />}
                <div className="card">
                    <h3>Split Result {session ? `(${session.splitSolutions.length} questions)` : ''}</h3>
                    {session && session.splitSolutions.length > 0 ? (
                        session.splitSolutions.map((a, i) => (
                            <SplitSolutionCard key={i} a={a} questionText={questionTextByIndex.get(a.questionIndex)} />
                        ))
                    ) : (
                        <p className="muted">
                            {source === 'unavailable'
                                ? 'No durable record for this run — it predates run records, and its upload session has expired. The run summary and the raw LLM output below are still available.'
                                : 'No split data recorded for this run.'}
                        </p>
                    )}
                </div>
                {session?.pageReading && <SolutionPageReading reading={session.pageReading} />}
                </>
            }
            runs={<>
                {runs.length > 0 ? (
                    <RunsTable
                        rows={runs}
                        columns={runColumns}
                        rowKey={r => r.id}
                        selectedRowId={selectedRunId}
                        onSelect={r => setSelectedRunId(r.id)}
                        getRowClass={r => [r.isPrimary ? 'primary' : '', r.errors?.length ? 'has-error' : ''].filter(Boolean).join(' ')}
                    />
                ) : (
                    <div className="card"><p className="muted">No model runs recorded for this run.</p></div>
                )}
            </>}
            runDetail={
                selectedRun && (
                    <RunDetailCard
                        heading={runDetailHeading}
                        error={runError}
                        errorRecords={selectedRun.errorRecords}
                        summary={runSummary}
                        systemPrompt={selectedRun.systemPromptText}
                        userPrompt={selectedRun.promptText}
                        rawResponse={selectedRunRawResponse}
                        rawOutputFull={selectedRun.rawOutputFull}
                        reasoningSummary={selectedRun.reasoningSummaryText}
                        modelInputId={selectedRun.inputId}
                        modelOutputId={selectedRun.id}
                        parsed={parsedBody}
                    />
                )
            }
        />
        {correcting && correctionTarget && (
            <RegionCorrector
                questions={(session?.questions ?? []).map(q => ({
                    questionId: q.questionId,
                    questionIndex: q.questionIndex,
                    questionText: q.questionText,
                }))}
                resolveUpload={() => correctionApi(correctionTarget.studentId)
                    .resolveUpload(correctionTarget.testSessionId, attempt?.uploadId)}
                // Whose side, and a note for our records — see analysis-adjustments-target.md §4e.
                askWhy
                correctRegion={input => correctionApi(correctionTarget.studentId)
                    .correctRegion(correctionTarget.testSessionId, input)}
                onClose={() => setCorrecting(false)}
            />
        )}
        </>
    );
}
